/**
 * A view of a bunch of notes with an add button.  Shown on Overview screens and sometimes in dialogs.
 */

import Backbone from 'backbone'

import template from './notes-view.hbs'
import { Note } from 'scoreboard/notes/notes'
import NoteForm from './note-form'
import ReplyForm from './reply-form'
import momentHelper from 'scoreboard/components/moment-helper'
import _ from 'lodash'
import appDisplay from 'scoreboard/components/app-display'
import NoteManager from 'scoreboard/notes/note-manager'
import escapePreserveLineBreaks from 'scoreboard/components/escape-preserve-line-breaks'
import center from 'scoreboard/components/min-height-vertical-center-msg.hbs'
import i18n from 'scoreboard/components/i18n'
import layoutManager from 'scoreboard/components/layout-manager'
import sortNotesNewestFirst from './sort-notes-newest-first'
import canCreateNotes from './can-create-notes'
import conductor from 'scoreboard/components/conductor'
import urlsToLinks from 'scoreboard/components/urls-to-links'
import DateTooltips from 'scoreboard/components/date-tooltips'

var NotesView = Backbone.View.extend({

	tagName: 'ul',
	events: {
		'click .note-preview': 'edit',
		'click .note-preview a': '_onClickLink'
	},

	/**
	 * @param {ScorecardNode|Initiative} options.model
	 *
	 * @param {String} options.timeLabel label to show in the upper right corner
	 *
	 * @param {Number|Date} options.defaultTime the default time for the period selector when
	 * creating a new note.  it's either a periodId for scorecards, or a date for initiatives
	 *
	 * @param {Function} [options.filter] a filter for which notes should be displayed
	 *
	 * @param {boolean} [options.disabled] indicates whether interactions should be disabled
	 * (ie whether clicking on notes pops up the dialog)
	 *
	 * @param {boolean} [options.alwaysShowDomainObject=false]
	 * see ReplyForm's alwaysShowDomainObject option
	 *
	 * @param {Boolean} [options.guard=true] Pass false to prevent any guards from
	 * being applied (e.g. the reply form)
	 *
	 * @constructs
	 */
	initialize: function(options) {
		this.options = _.extend({
			showAddButton: true,
			showReplies: true,
			showModifyDate: true,
			disabled: false,
			guard: true
		}, options)
		this.noteManager = new NoteManager(this.model)
	},

	render: function() {
		// context is an array of notes
		var context = _.cloneDeep(this.model.attributes.notes)
		if (this.options.filter) {
			context = _.filter(context, this.options.filter)
		}

		context = sortNotesNewestFirst(context)

		_.each(context, function(note) {
			// preserve line breaks
			if (!note.html) {
				note.text = urlsToLinks(escapePreserveLineBreaks(note.text))
			}
			note.createdLabel = momentHelper.fromNow(note.created)
		})

		this.$el.empty().append(template(context))
		this.secure()
		// after emptying view need to re-delegate events
		this.delegateEvents()

		// #50109 provide a tooltip when hovering over a date description to
		// display the full date
		this._dateTooltips = new DateTooltips({ el: this.el })

		this.$('.add-note').toggleClass('is-not-displayed', !this.options.showAddButton)
		this.$el.toggleClass('is-hiding-notes', !this.options.showReplies && !this.options.showModifyDate)
		this.$el.toggleClass('is-hiding-modify-date', !this.options.showModifyDate)
		this.$('.note-preview .comments').toggleClass('is-not-displayed', !this.options.showReplies)

		return this
	},

	secure: function() {
		if (!this.canCreateNotes() || this.model.get('pendingDeletion')) {
			// remove rather than hide so the correct number of notes is shown
			// in the "show 19 more notes" message
			this.$('.add-note').remove()
			if (!this.hasNotes()) {
				this.$el.append(center(i18n.get('common.noNotes'))).addClass('no-notes')
			}
		}
	},

	canCreateNotes: function () {
		return canCreateNotes(this.options.model)
	},

	hasNotes: function() {
		// initiative model will return all the notes for the initiative, so filter them
		// if the filter is undefined (as is the case for metrics) , _.filter will return the entire number of notes
		return _.filter(this.model.get('notes'), this.options.filter).length > 0
	},

	_noteDomainObject: function (note) {
		if (note.get('domainId') != this.model.id && this.options.otherDomainObjects) {
			return this.options.otherDomainObjects[note.get('domainId')]
		}

		return this.model
	},

	edit: function(e) {
		const noteId = this.$(e.currentTarget).data('id')
		// #58473
		// Shared dashboards and forms (with dashboard widgets) should prompt
		// user login when trying to add or edit a note
		if (layoutManager.sharedResource) {
			this._onNoteDrillDown(noteId)
		} else if (!this.options.disabled) {
			this.displayNote(noteId)
		}
	},

	// https://github.com/SpiderStrategies/Scoreboard/issues/28323#issuecomment-582143709
	_onNoteDrillDown: function (noteId) {
		const section = this.model.section
		const note = this.noteManager.note(noteId)
		if (noteId) {
			const domainId = this._noteDomainObject(note).id
			conductor.navigate(`${section}/${domainId}/note/${noteId}`)
		} else {
			conductor.navigate(`${section}/${this.model.id}/note`)
		}
	},

	_onClickLink: function(e) {
		//going to link in the note should prevent the note edit feature
		e.stopPropagation()
	},

	/**
	 * @param id the ID of note to display
	 * @param replyId identifies the note reply to be edited (currently does nothing if noteAction !== 'edit')
	 * @param {'view' | 'edit' | 'new' | 'delete'} noteAction
	 */
	displayNote(id, replyId, noteAction) {
		let form, note
		// if it's an existing note
		if (id) {
			note = this.noteManager.note(id)
			note.section = this.model.section

			form = new ReplyForm({
				model: note,
				rootDomainObject: this.model,
				noteDomainObject: this._noteDomainObject(note),
				alwaysShowReplyForm: true,
				alwaysShowDomainObject: this.options.alwaysShowDomainObject,
				guard: this.options.guard,
				useSourceNotes: this.model.get('type') === 'linked' && this.model.get('useSourceNotes')
			})

			form.on('replies:add', function(options) {
				this.noteManager.setNote(note)

				// Using scrollTop instead of scrollIntoView because scrollIntoView tends to scroll
				// the wrong div
				form.$('.dialog-body').scrollTop(options.el.offsetTop)
			}.bind(this))
		} else {
			// #32577 if scorecard node is linked and using source notes,
			// note should be created on source node
			let domainId = this.model.get('type') === 'linked' && this.model.get('useSourceNotes') ?
				this.model.attributes.sourceId : this.model.id
			note = new Note({domainId: domainId})
			note.section = this.model.section
			form = new NoteForm({
				model: note,
				domainObject: this.model,
				defaultTime: this.options.defaultTime
			})
		}

		// re-render view when notes change or are deleted
		note.on('sync update', () => {
			this.noteManager.setNote(note)
			this.trigger('change:notes', this.noteManager.model.get('notes')
				.length)
		})

		note.once('destroy', n => {
			this.noteManager.deleteNote(n)
			this.trigger('change:notes', this.noteManager.model.get('notes')
				.length)
		})

		appDisplay.dialog(form)

		if (id && noteAction === 'edit') {
			if (!replyId) {
				form.editNote()
			} else {
				form.toggleNewReplyForm(false)
				form.editReply(replyId)
			}
		} else if (id && noteAction === 'delete') {
			form.deleteReply(replyId)
		}
	},

	disable(disable) {
		this.options.disabled = disable
	},

	remove() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this._dateTooltips], 'remove')
	}
})

export default NotesView

NotesView.__filename__ = 'notes/notes-view.js'
